import { useState, useEffect, useCallback, useRef } from 'react'
import useMenuBack from '../../hooks/useMenuBack'
import { ArrowLeft, Check, CheckCircle, Calendar, UserPlus, Clock, Activity, User, Info, MessageSquare } from 'lucide-react'
import { notificationsAPI } from '../../services/api'
import { ensurePushSubscribed } from '../../services/push'
import { isNativePush, startNativePush } from '../../services/nativePush'
import { useI18n } from '../../i18n'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

/**
 * NotificationPreferencesScreen v220.12
 *
 * Brand-correct redesign (was v179.0.0).
 *
 * Changes from v179:
 *  - Header save-state pill with idle/saving/saved transitions + haptic feedback
 *  - All category icons now use brand palette (Navy/Blue/Light Blue/neutral) — no green or amber
 *  - Added Messages category for case_message events
 *  - Sleeker card chrome — single hairline border, 14px radius
 *  - Pills use Check icon when active (was text ✓)
 *  - Three sections: Care updates · Monitoring · Account
 *
 * Categories on PUT/GET:
 *   care_updates  — case_finalized, case_accepted
 *   appointments  — appointment_* events
 *   referrals     — referral_created
 *   messages      — case_message (NEW in v220.12)
 *   monitoring    — observation_check_in, follow_up_ready
 *   case_activity — case_sent, case_reopened, new_case, pre_visit_submitted
 *   account       — welcome, profile updates
 */

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE_BLUE = '#E9F8FF'
const GREEN_SAVED = '#2F8C3A'
const GREEN_SAVED_BG = 'rgba(102,209,112,0.18)'

// Category definitions with display info — all brand-palette icons
const CATEGORIES = [
  {
    section: 'notifications.sectionCareUpdates',
    items: [
      { key: 'care_updates', icon: CheckCircle, iconBg: ICE_BLUE, iconColor: NAVY, titleKey: 'notifications.catCareUpdates', descKey: 'notifications.catCareUpdatesDesc' },
      { key: 'appointments', icon: Calendar, iconBg: 'rgba(70,168,249,0.12)', iconColor: BLUE, titleKey: 'notifications.catAppointments', descKey: 'notifications.catAppointmentsDesc' },
      { key: 'referrals', icon: UserPlus, iconBg: ICE_BLUE, iconColor: BLUE, titleKey: 'notifications.catReferrals', descKey: 'notifications.catReferralsDesc' },
      { key: 'messages', icon: MessageSquare, iconBg: 'rgba(70,168,249,0.12)', iconColor: BLUE, titleKey: 'notifications.catMessages', descKey: 'notifications.catMessagesDesc' },
    ],
  },
  {
    section: 'notifications.sectionMonitoring',
    items: [
      { key: 'monitoring', icon: Clock, iconBg: 'rgba(70,168,249,0.12)', iconColor: BLUE, titleKey: 'notifications.catMonitoring', descKey: 'notifications.catMonitoringDesc' },
    ],
  },
  {
    section: 'notifications.sectionAccount',
    items: [
      { key: 'case_activity', icon: Activity, iconBg: '#F1F5F9', iconColor: '#64748B', titleKey: 'notifications.catCaseActivity', descKey: 'notifications.catCaseActivityDesc' },
      { key: 'account', icon: User, iconBg: '#F1F5F9', iconColor: '#64748B', titleKey: 'notifications.catAccount', descKey: 'notifications.catAccountDesc' },
    ],
  },
]

// Default preferences — mirror server DEFAULT_CATEGORY_PREFS (index.py).
// push = the in-app/device channel: on means in-app feed AND browser push.
const DEFAULT_PREFS = {
  care_updates:  { email: true, sms: true,  push: true },
  appointments:  { email: true, sms: true,  push: true },
  referrals:     { email: true, sms: true,  push: true },
  messages:      { email: true, sms: true,  push: true },   // v220.12 — NEW category
  monitoring:    { email: true, sms: true,  push: true },
  case_activity: { email: true, sms: true,  push: false },
  account:       { email: false, sms: true,  push: false },
}

function ChannelPill({ label, active, locked, onToggle }) {
  const showCheck = locked || active
  return (
    <button
      onClick={locked ? undefined : onToggle}
      style={{
        fontFamily: 'Inter, sans-serif',
        fontSize: 9.5, fontWeight: 600,
        padding: '4px 10px', borderRadius: 999,
        border: `1px solid ${locked || active ? BLUE : '#E2E8F0'}`,
        background: locked || active ? 'rgba(70,168,249,0.10)' : '#F8FAFC',
        color: locked || active ? BLUE : '#94A3B8',
        cursor: locked ? 'default' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        transition: 'all 0.15s',
      }}
    >
      {showCheck && <Check size={9} strokeWidth={3} />}
      {label}
    </button>
  )
}

function CategoryCard({ category, prefs, onToggle, t }) {
  const { key, icon: Icon, iconBg, iconColor, titleKey, descKey } = category
  // Merge defaults so every channel key (email/sms/push) resolves even for
  // preferences saved before the push channel existed.
  const catPrefs = { ...(DEFAULT_PREFS[key] || { email: true, sms: false, push: false }), ...(prefs[key] || {}) }

  return (
    <div style={{
      margin: '0 18px 6px',
      padding: '14px 16px',
      background: '#fff',
      border: '1px solid #F1F5F9',
      borderRadius: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 10, background: iconBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon size={16} color={iconColor} strokeWidth={2} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: NAVY, lineHeight: 1.2 }}>{t(titleKey)}</div>
          <div style={{ fontSize: 10.5, color: '#94A3B8', marginTop: 2, lineHeight: 1.3 }}>{t(descKey)}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, paddingLeft: 44 }}>
        <ChannelPill
          label={t('notifications.channelPush') || 'Push'}
          active={catPrefs.push !== false}
          onToggle={() => onToggle(key, 'push')}
        />
        <ChannelPill
          label={t('notifications.channelEmail')}
          active={catPrefs.email}
          onToggle={() => onToggle(key, 'email')}
        />
        <ChannelPill
          label={t('notifications.channelSms')}
          active={catPrefs.sms}
          onToggle={() => onToggle(key, 'sms')}
        />
      </div>
    </div>
  )
}

function SaveStatusPill({ status, t }) {
  if (status === 'saving') {
    return (
      <span style={{
        fontSize: 10.5, fontWeight: 600,
        padding: '4px 10px', borderRadius: 999,
        background: ICE_BLUE, color: BLUE,
        display: 'inline-flex', alignItems: 'center', gap: 5,
      }}>
        <OrellaSpinner size={16} tone="inherit" />
        {t('common.saving') || 'Saving'}
      </span>
    )
  }
  if (status === 'saved') {
    return (
      <span style={{
        fontSize: 10.5, fontWeight: 600,
        padding: '4px 10px', borderRadius: 999,
        background: GREEN_SAVED_BG, color: GREEN_SAVED,
        display: 'inline-flex', alignItems: 'center', gap: 5,
      }}>
        <Check size={11} strokeWidth={3} />
        {t('common.saved') || 'Saved'}
      </span>
    )
  }
  if (status === 'error') {
    return (
      <span style={{
        fontSize: 10.5, fontWeight: 600,
        padding: '4px 10px', borderRadius: 999,
        background: 'rgba(239,68,68,0.12)', color: '#DC2626',
        display: 'inline-flex', alignItems: 'center',
      }}>
        {t('common.saveFailed') || 'Save failed'}
      </span>
    )
  }
  return null
}

export default function NotificationPreferencesScreen() {
  const goBack = useMenuBack()
  const { t } = useI18n()
  const [prefs, setPrefs] = useState(DEFAULT_PREFS)
  const [loading, setLoading] = useState(true)
  const [saveStatus, setSaveStatus] = useState(null) // null | 'saving' | 'saved' | 'error'
  const savedTimerRef = useRef(null)

  // Load preferences
  useEffect(() => {
    (async () => {
      try {
        const result = await notificationsAPI.getPreferences()
        if (result.categoryPreferences && Object.keys(result.categoryPreferences).length > 0) {
          // Deep-merge per category so every category carries email/sms/push,
          // even prefs saved before the push channel existed.
          const merged = { ...DEFAULT_PREFS }
          for (const k of Object.keys(result.categoryPreferences)) {
            merged[k] = { ...(DEFAULT_PREFS[k] || {}), ...result.categoryPreferences[k] }
          }
          setPrefs(merged)
        }
      } catch (err) {
        console.error('Failed to load notification preferences:', err)
      } finally {
        setLoading(false)
      }
    })()
    return () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    }
  }, [])

  // Toggle a channel for a category and auto-save with explicit feedback.
  // Push is special: enabling it must first secure OS permission + a live browser
  // subscription. If that fails, the pill does not flip on. Disabling push only
  // updates the per-category pref — the device stays subscribed for other
  // categories, and the in-app feed is always recorded regardless.
  const handleToggle = useCallback(async (categoryKey, channel) => {
    const nextVal = !prefs[categoryKey]?.[channel]

    if (channel === 'push' && nextVal) {
      setSaveStatus('saving')
      // s119 (D532): on the iPhone the pill asks iOS (APNs); in a browser, the browser push.
      const res = isNativePush() ? await startNativePush() : await ensurePushSubscribed()
      if (!res.ok) {
        // Denied / unsupported — leave the pill off and surface the failure.
        setSaveStatus('error')
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
        savedTimerRef.current = setTimeout(() => setSaveStatus(null), 2500)
        return
      }
    }

    const updated = {
      ...prefs,
      [categoryKey]: { ...prefs[categoryKey], [channel]: nextVal },
    }
    setPrefs(updated)

    if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    setSaveStatus('saving')
    notificationsAPI.updatePreferences({ categoryPreferences: updated })
      .then(() => {
        setSaveStatus('saved')
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
          try { navigator.vibrate(8) } catch (e) { /* noop */ }
        }
        savedTimerRef.current = setTimeout(() => setSaveStatus(null), 1800)
      })
      .catch(err => {
        console.error('Failed to save preferences:', err)
        setSaveStatus('error')
        savedTimerRef.current = setTimeout(() => setSaveStatus(null), 2500)
      })
  }, [prefs])

  return (
    <div className="min-h-full bg-white flex flex-col">
      {/* Header */}
      <div className="px-4 pt-4 pb-2 flex items-center gap-2.5">
        <button
          onClick={() => goBack()}
          className="p-0"
          style={{ background: 'none', border: 'none' }}
        >
          <ArrowLeft size={18} color="#64748B" strokeWidth={2.5} />
        </button>
        <h1 className="flex-1" style={{ fontSize: 17, fontWeight: 700, color: NAVY, margin: 0 }}>
          {t('notifications.preferencesTitle')}
        </h1>
        <SaveStatusPill status={saveStatus} t={t} />
      </div>

      {loading ? (
        <div className="mt-16 flex flex-col items-center justify-center">
          <OrellaSpinner size={32} />
        </div>
      ) : (
        <div className="flex-1 pb-8 overflow-auto">
          {/* Info banner */}
          <div style={{
            margin: '6px 18px 14px',
            background: ICE_BLUE,
            borderRadius: 12,
            padding: '10px 12px',
            fontSize: 11,
            color: NAVY,
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            lineHeight: 1.4,
          }}>
            <Info size={14} color={BLUE} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{t('notifications.preferencesInfo')}</span>
          </div>

          {CATEGORIES.map((section, si) => (
            <div key={si}>
              <p style={{
                fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.12em', color: '#94A3B8',
                padding: '14px 20px 8px',
              }}>
                {t(section.section)}
              </p>
              {section.items.map(cat => (
                <CategoryCard
                  key={cat.key}
                  category={cat}
                  prefs={prefs}
                  onToggle={handleToggle}
                  t={t}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
